import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError, gbp } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

function todayISO() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
}

const FIELDS = [
  { key: "account", label: "Account" },
  { key: "cash", label: "Cash" },
  { key: "deliveroo", label: "Deliveroo" },
  { key: "just_eat_card", label: "Just Eat Card" },
  { key: "paypal", label: "PayPal" },
  { key: "pdq", label: "PDQ" },
  { key: "uber_eats", label: "Uber Eats" },
  { key: "webcard", label: "Webcard" },
];

const blank = () => ({
  net_sales: "", account: "", cash: "", deliveroo: "", just_eat_card: "",
  paypal: "", pdq: "", uber_eats: "", webcard: "",
});

export default function ManagerSales() {
  const [date, setDate] = useState(todayISO());
  const [form, setForm] = useState(blank());
  const [existingId, setExistingId] = useState(null);
  const [recent, setRecent] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sales", { params: { start_date: date, end_date: date } });
      const today = data.sales.find((s) => s.date === date);
      if (today) {
        setExistingId(today.id);
        setForm({
          net_sales: String(today.net_sales ?? ""),
          ...Object.fromEntries(FIELDS.map((f) => [f.key, String(today[f.key] ?? "")])),
        });
      } else {
        setExistingId(null);
        setForm(blank());
      }
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }, [date]);

  const loadRecent = useCallback(async () => {
    try {
      const end = todayISO();
      const start = new Date(); start.setDate(start.getDate() - 14);
      const startISO = start.toISOString().slice(0, 10);
      const { data } = await api.get("/sales", { params: { start_date: startISO, end_date: end } });
      setRecent(data.sales);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  const tendered = useMemo(() => {
    return FIELDS.reduce((acc, f) => acc + (parseFloat(form[f.key] || "0") || 0), 0);
  }, [form]);

  const variance = useMemo(() => tendered - (parseFloat(form.net_sales || "0") || 0), [tendered, form.net_sales]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { date, net_sales: parseFloat(form.net_sales || "0") };
      FIELDS.forEach((f) => { body[f.key] = parseFloat(form[f.key] || "0"); });
      await api.post("/sales", body);
      toast.success(existingId ? "Sales updated" : "Sales saved");
      load();
      loadRecent();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sales entry?")) return;
    try {
      await api.delete(`/sales/${id}`);
      toast.success("Deleted");
      load();
      loadRecent();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px]">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="label-uc">Daily Sales</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Tender breakdown</h1>
          <p className="text-sm text-zinc-500 mt-1">Log net sales and the value taken via each payment method.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="sdate" className="label-uc">Date</Label>
          <Input id="sdate" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="rounded-none border-zinc-300 w-44 font-mono-data" data-testid="sales-date-filter" />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={save} className="lg:col-span-1 bg-white border border-zinc-200 p-5" data-testid="sales-form">
          <div className="label-uc mb-3">{existingId ? "Edit" : "Add"} entry — {date}</div>

          <div>
            <Label className="label-uc">Net sales</Label>
            <Input type="number" step="0.01" min="0" value={form.net_sales}
              onChange={(e) => setForm({ ...form, net_sales: e.target.value })}
              className="rounded-none border-zinc-300 mt-1.5 font-mono-data text-lg" required data-testid="sales-net-input" />
          </div>

          <div className="mt-5 pt-4 border-t border-zinc-200">
            <div className="label-uc mb-2">Tender breakdown (£)</div>
            <div className="grid grid-cols-2 gap-3">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="label-uc text-[10px]">{f.label}</Label>
                  <Input type="number" step="0.01" min="0" value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="rounded-none border-zinc-300 mt-1 font-mono-data h-9"
                    data-testid={`sales-${f.key}-input`} />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-zinc-200 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-600">Tendered total</span>
              <span className="font-mono-data font-semibold" data-testid="sales-tendered">{gbp(tendered)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-600">Variance vs net sales</span>
              <span className={`font-mono-data font-semibold ${Math.abs(variance) < 0.01 ? "text-emerald-700" : "text-[#DF1A22]"}`} data-testid="sales-variance">
                {gbp(variance)}
              </span>
            </div>
          </div>

          <Button type="submit" disabled={saving} className="rounded-none bg-zinc-950 hover:bg-zinc-800 h-11 w-full mt-5" data-testid="sales-submit-button">
            <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : (existingId ? "Update sales" : "Save sales")}
          </Button>
        </form>

        <div className="lg:col-span-2 bg-white border border-zinc-200">
          <div className="px-5 py-4 border-b border-zinc-200">
            <div className="label-uc">Last 14 days</div>
            <div className="font-display text-xl font-bold mt-0.5" data-testid="sales-recent-count">{recent.length} entries</div>
          </div>
          <div className="overflow-x-auto" data-testid="sales-recent-table">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left">
                  <th className="px-3 py-2 label-uc">Date</th>
                  <th className="px-3 py-2 label-uc text-right">Net sales</th>
                  <th className="px-3 py-2 label-uc text-right">Tendered</th>
                  <th className="px-3 py-2 label-uc text-right">Variance</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-12 text-center text-zinc-400">No recent entries.</td></tr>
                )}
                {recent.map((r) => {
                  const v = (r.tendered_total || 0) - (r.net_sales || 0);
                  return (
                    <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                      <td className="px-3 py-2 font-mono-data">{r.date}</td>
                      <td className="px-3 py-2 text-right font-mono-data">{gbp(r.net_sales)}</td>
                      <td className="px-3 py-2 text-right font-mono-data">{gbp(r.tendered_total)}</td>
                      <td className={`px-3 py-2 text-right font-mono-data ${Math.abs(v) < 0.01 ? "text-emerald-700" : "text-[#DF1A22]"}`}>{gbp(v)}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => remove(r.id)} className="text-zinc-400 hover:text-[#DF1A22]"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
